import React, { useEffect, useRef, useState } from "react";
import styles from "./invoice_supplier_section.module.css";
import {
    ActionButton,
    Content,
    Dialog,
    DialogContainer,
    Flex,
    Heading,
    Link,
    ProgressCircle,
    TextField,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import SupplierDroDown from "./formComponents/SupplierDropDown";
import { AIChatIcon } from "../../../../../components/protected/Icons/IconsLib";
import { Button } from "react-bootstrap";
import CustomerDroDown from "./formComponents/CustomerDropDown";
import UserDropDown from "./formComponents/UserDropDown";
import ContactDropDown from "./formComponents/ContactDropdown";

// ✅ added (same as supplier)
import { usePreloadAssets } from "../../../../../hooks/UsePreloadAssets";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { GetContactRequest } from "../../../../../services/soap/main/requests/GetContactRequest";
import { GetFolderRequest } from "../../../../../services/soap/project/requests/GetFolderRequest";
import { getInvoiceDocumentClass } from "../../../../../shared/invoice/invoiceTypeLogic";

interface IInvoiceCustomerSection {
    invoice: any;
    isEdit: any;
    onChange: (data: any) => void;
    SELECTED_INVOICE?: any;
}

const InvoiceCustomerSection: React.FC<IInvoiceCustomerSection> = ({
    invoice,
    isEdit,
    onChange,
    SELECTED_INVOICE,
}) => {
    const [showMorefileds, setShowMorefields] = useState<boolean>(false);
    const hydratedCustomerIdRef = useRef<string>("");
    const hydratingCustomerIdRef = useRef<string>("");

    const { addressTypes } = usePreloadAssets();
    const { store, mainService, projectService } = useDependency();

    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });
    const _tt = (key: string) => t(`invoice_common.${key}`, { ns: "invoice" });
    const invoiceClass = getInvoiceDocumentClass(invoice || SELECTED_INVOICE);
    const customerFpaTypeCategory =
        invoiceClass === "purchase"
            ? "com.atollon.project.fpatypecategory.organization"
            : "com.atollon.invoice.fpatypecategory.client";
    const resolvedSelectedCustomer =
        invoice?.customer ||
        (invoiceClass === "sales" ? invoice?.folder || SELECTED_INVOICE?.folder || "" : "");
    const resolvedCustomerName =
        invoice?.customerName ||
        (invoiceClass === "sales" ? invoice?.folderName || SELECTED_INVOICE?.folderName || "" : "");

    const updateInvoice = (updater: any) => {
        onChange((prev: any) => {
            const base = prev || {};
            return typeof updater === "function" ? updater(base) : updater;
        });
    };

    useEffect(() => {
        const customerId = String(resolvedSelectedCustomer || "").trim();
        if (!customerId || customerId === "0") return;
        if (hydratedCustomerIdRef.current === customerId) return;
        if (hydratingCustomerIdRef.current === customerId) return;

        const hasCustomerDetails = Boolean(
            invoice?.customerStreet ||
            invoice?.customerCity ||
            invoice?.customerZIP ||
            invoice?.customerRegion ||
            invoice?.customerCountry
        );
        if (hasCustomerDetails) {
            hydratedCustomerIdRef.current = customerId;
            return;
        }

        hydratingCustomerIdRef.current = customerId;
        handleCustomerChange({
            id: customerId,
            name: resolvedCustomerName,
        });
        // This hydrates form fields when customer comes from defaults/context, not manual selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedSelectedCustomer, resolvedCustomerName]);

    const handleShowMorefields = () => {
        setShowMorefields(!showMorefileds);
    };

    const addDaysToIsoDate = (isoDate: string, days: number): string => {
        const [year, month, day] = String(isoDate).split("-").map(Number);
        if (!year || !month || !day) return isoDate;
        const base = new Date(Date.UTC(year, month - 1, day));
        if (Number.isNaN(base.getTime())) return isoDate;
        base.setUTCDate(base.getUTCDate() + Math.max(0, days));
        const y = base.getUTCFullYear();
        const m = String(base.getUTCMonth() + 1).padStart(2, "0");
        const d = String(base.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const getTodayIso = (): string => new Date().toISOString().split("T")[0];

    const normalizeAddresses = (addressesWrapper: any): any[] => {
        if (!addressesWrapper) return [];

        let raw = addressesWrapper;

        // ADDRESSES: { ADDRESS: ... }
        if (typeof raw === "object" && !Array.isArray(raw) && raw.ADDRESS) {
            raw = raw.ADDRESS;
        }

        const list = Array.isArray(raw) ? raw : [raw];
        return list.map((x) => x?.ADDRESS ?? x).filter(Boolean);
    };

    const pickAddress = (
        entityType: "COMPANY" | "PERSON",
        addressesWrapper: any
    ) => {
        const list = normalizeAddresses(addressesWrapper);
        if (!list.length) return null;

        const isPrimary = (a: any) => String(a?.isPrimary) === "1";

        const matchByAddressTypes = (a: any) => {
            const typeId = String(a?.type ?? "");
            const at = (addressTypes as any)?.[typeId];
            if (!at) return false;

            return entityType === "COMPANY"
                ? String(at?.invoiceAddress) === "1"
                : String(at?.permanentAddress) === "1";
        };

        // 1) exact match
        const exact = list.find(matchByAddressTypes);
        if (exact) return exact;

        // 2) primary
        const primary = list.find(isPrimary);
        if (primary) return primary;

        // 3) index 0
        return list[0];
    };

    const handleCustomerChange = async (data: any) => {
        const baseType = String(invoice?.baseType || SELECTED_INVOICE?.baseType || "");
        const isInvoiceDoc = baseType === "1";
        if (!data) {
            hydratedCustomerIdRef.current = "";
            hydratingCustomerIdRef.current = "";
            const fallbackDueDays = Math.max(0, Number(invoice?.invoiceDefaultDueDays) || 0);
            const issueDate = String(invoice?.issuedDate || getTodayIso());
            const dueDate = addDaysToIsoDate(issueDate, fallbackDueDays);
            updateInvoice((prev: any) => ({
                ...prev,
                customer: "",
                customerName: "",
                customerStreet: "",
                customerCity: "",
                customerZIP: "",
                customerRegion: "",
                customerCountry: "",
                customerDueDays: fallbackDueDays,
                ...(isInvoiceDoc ? { dueDate } : {}),
            }));
            return;
        }
        const response: any = await mainService.getContact(
            new GetContactRequest(
                store.Server,
                store.SessionId,
                undefined,
                undefined,
                undefined,
                data.id
            )
        );

        let nextValues: any = null;
        if (response?.result === "OK") {
            const isPerson = !!response?.PERSON;
            const entity = isPerson ? response.PERSON : response.COMPANY;
            const entityType: "PERSON" | "COMPANY" = isPerson ? "PERSON" : "COMPANY";
            const contactDueDaysRaw = entity?.dueDays ?? response?.dueDays;
            const contactDueDays = Math.max(0, Number(contactDueDaysRaw) || 0);
            const fallbackDueDays = Math.max(0, Number(invoice?.invoiceDefaultDueDays) || 0);
            const dueDays = contactDueDays > 0 ? contactDueDays : fallbackDueDays;
            const issueDate = String(invoice?.issuedDate || getTodayIso());
            const calculatedDueDate = addDaysToIsoDate(issueDate, dueDays);

            const address = pickAddress(entityType, entity?.ADDRESSES);
            nextValues = {
                customer: data.id,
                customerName: data?.name ?? entity?.name1 ?? entity?.name ?? "",
                customerStreet: address?.street ?? entity?.street ?? "",
                customerCity: address?.cityName ?? address?.city ?? entity?.city ?? "",
                customerZIP: address?.zipName ?? address?.zip ?? entity?.zip ?? "",
                customerRegion: address?.regionName ?? address?.region ?? entity?.region ?? "",
                customerCountry: address?.countryName ?? address?.country ?? entity?.country ?? "",
                customerDueDays: dueDays,
            };

            if (isInvoiceDoc) {
                nextValues.dueDate = calculatedDueDate;
            }

            const hasAddressData = Boolean(
                nextValues?.customerStreet ||
                nextValues?.customerCity ||
                nextValues?.customerZIP ||
                nextValues?.customerRegion ||
                nextValues?.customerCountry
            );

            if (!hasAddressData) {
                try {
                    const folderResponse: any = await projectService.getFolder(
                        new GetFolderRequest(store.Server, store.SessionId, Number(data.id))
                    );
                    if (folderResponse?.result === "OK") {
                        const folder = folderResponse?.FOLDER;
                        const company = folder?.CONTACT?.COMPANY;
                        const rawAddresses = company?.ADDRESSES?.ADDRESS;
                        const folderAddress = Array.isArray(rawAddresses)
                            ? rawAddresses.find((a: any) => String(a?.isPrimary ?? "") === "1") || rawAddresses[0]
                            : rawAddresses || {};

                        nextValues = {
                            ...nextValues,
                            customerStreet: folderAddress?.street ?? company?.street ?? "",
                            customerCity: folderAddress?.cityName ?? folderAddress?.city ?? company?.city ?? "",
                            customerZIP: folderAddress?.zipName ?? folderAddress?.zip ?? company?.zip ?? "",
                            customerRegion: folderAddress?.regionName ?? folderAddress?.region ?? company?.region ?? "",
                            customerCountry: folderAddress?.countryName ?? folderAddress?.country ?? company?.country ?? "",
                        };
                    }
                } catch {
                    // Keep current values if folder fallback fails.
                }
            }
        } else {
            try {
                const folderResponse: any = await projectService.getFolder(
                    new GetFolderRequest(store.Server, store.SessionId, Number(data.id))
                );
                if (folderResponse?.result === "OK") {
                    const folder = folderResponse?.FOLDER;
                    const company = folder?.CONTACT?.COMPANY;
                    const rawAddresses = company?.ADDRESSES?.ADDRESS;
                    const address = Array.isArray(rawAddresses)
                        ? rawAddresses.find((a: any) => String(a?.isPrimary ?? "") === "1") || rawAddresses[0]
                        : rawAddresses || {};
                    const fallbackDueDays = Math.max(0, Number(invoice?.invoiceDefaultDueDays) || 0);
                    const issueDate = String(invoice?.issuedDate || getTodayIso());

                    nextValues = {
                        customer: String(data.id ?? ""),
                        customerName: data?.name ?? folder?.name ?? folder?.name1 ?? company?.name1 ?? company?.name ?? "",
                        customerStreet: address?.street ?? company?.street ?? "",
                        customerCity: address?.cityName ?? address?.city ?? company?.city ?? "",
                        customerZIP: address?.zipName ?? address?.zip ?? company?.zip ?? "",
                        customerRegion: address?.regionName ?? address?.region ?? company?.region ?? "",
                        customerCountry: address?.countryName ?? address?.country ?? company?.country ?? "",
                        customerDueDays: fallbackDueDays,
                    };

                    if (isInvoiceDoc) {
                        nextValues.dueDate = addDaysToIsoDate(issueDate, fallbackDueDays);
                    }
                }
            } catch {
                nextValues = null;
            }
        }

        if (!nextValues) {
            hydratedCustomerIdRef.current = String(data.id ?? "");
            hydratingCustomerIdRef.current = "";
            return;
        }

        updateInvoice((prev: any) => ({
            ...prev,
            ...nextValues,
        }));
        hydratedCustomerIdRef.current = String(data.id ?? "");
        hydratingCustomerIdRef.current = "";
    };

    function CustomerModal() {
        return (
            <>
                <Dialog
                    width={{ L: "800px", base: "100%" }}
                    aria-label={_t(isEdit ? "update_invoice" : "add_invoice")}
                    isDismissable
                    onDismiss={() => setShowMorefields(false)}
                >
                    <Heading level={2}>{_t(isEdit ? "customer" : "customer")}</Heading>
                    <Content>
                        <Flex
                            direction={{ base: "column", L: "row" }}
                            gap={"size-150"}
                            marginTop={"size-150"}
                        >
                            <CustomerDroDown
                                selectedCustomer={resolvedSelectedCustomer}
                                CustomerName={resolvedCustomerName}
                                isEdit={isEdit}
                                fpaTypeCategory={customerFpaTypeCategory}
                                onChange={(data) => {
                                    handleCustomerChange(data);
                                }}
                            />
                            <TextField
                                width={"100%"}
                                label={_t("street")}
                                value={invoice?.customerStreet}
                                onChange={(e: any) =>
                                    onChange({ ...invoice, customerStreet: e })
                                }
                            />
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <ContactDropDown
                                selectedContact={invoice?.customerContact}
                                contactName={invoice?.customerContactName}
                                isEdit={isEdit}
                                label={_t("contact")}
                                width={{ base: "100%", L: "52%" }}
                                onChange={(data) => {
                                    onChange({
                                        ...invoice,
                                        customerContact: data?.id,
                                        customerContactName: data?.name,
                                    });
                                }}
                            />
                            <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                                <TextField
                                    width={{ base: "100%", L: "60" }}
                                    label={_t("city")}
                                    value={invoice?.customerCity}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, customerCity: e })
                                    }
                                />
                                <TextField
                                    width={{ base: "100%", L: "40" }}
                                    label={_t("zip_code")}
                                    value={invoice?.customerZIP}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, customerZIP: e })
                                    }
                                />
                            </Flex>
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <TextField
                                width={{ base: "100%", L: "52%" }}
                                label={_t("name")}
                                value={invoice?.customerName || resolvedCustomerName || ""}
                                onChange={(e: any) =>
                                    onChange({ ...invoice, customerName: e })
                                }
                            />
                            <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                                <TextField
                                    width={{ base: "100%", L: "60" }}
                                    label={_t("region")}
                                    value={invoice?.customerRegion}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, customerRegion: e })
                                    }
                                />
                                <TextField
                                    width={{ base: "100%", L: "40" }}
                                    label={_t("country")}
                                    value={invoice?.customerCountry}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, customerCountry: e })
                                    }
                                />
                            </Flex>
                        </Flex>
                    </Content>
                </Dialog>

                <Flex
                    UNSAFE_style={{
                        backgroundColor: "white",
                        justifyContent: "end",
                        width: "100%",
                        padding: "1rem",
                        paddingLeft: 55,
                        paddingRight: 50,
                        boxShadow: "0 -1px 1px rgba(0,0,0,0.1)",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 1,
                    }}
                >
                    <Flex gap={"size-200"} UNSAFE_className={styles.button_container}>
                        <Button
                            variant="danger"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_cancel}`}
                        >
                            {_tt("button_cancel")}
                        </Button>
                        <Button
                            type="submit"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_add}`}
                        >
                            {_tt("button_ok")}
                        </Button>
                    </Flex>
                </Flex>
            </>
        );
    }

    return (
        <Flex
            direction={{ base: "column", L: "column" }}
            UNSAFE_className={styles.main_container}
        >
            <Flex justifyContent={"end"} UNSAFE_className={styles.toggle_row}>
                <Link
                    UNSAFE_className={styles.show_more_button}
                    onPress={handleShowMorefields}
                    isQuiet
                >
                    {_tt("show_more")}
                </Link>
            </Flex>

            <CustomerDroDown
                selectedCustomer={resolvedSelectedCustomer}
                CustomerName={resolvedCustomerName}
                isEdit={isEdit}
                fpaTypeCategory={customerFpaTypeCategory}
                onChange={(data) => {
                    handleCustomerChange(data);
                }}
            />

            <DialogContainer onDismiss={() => setShowMorefields(false)}>
                {showMorefileds && CustomerModal()}
            </DialogContainer>
        </Flex>
    );
};

export default InvoiceCustomerSection;
